﻿import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, X, AlertCircle } from 'lucide-react';
import { PERMISSIONS } from '@pms/shared';
import {
  createDmPageSchema, updateDmPageSchema,
  createDmShootingCategorySchema,
  createDmDesignCategorySchema,
} from '@pms/shared';
import type {
  CreateDmPageInput, UpdateDmPageInput,
  CreateDmShootingCategoryInput,
  CreateDmDesignCategoryInput,
  AddDmDesignCategorySizeInput,
  AddDmAccessUserInput,
  DmAccessType,
} from '@pms/shared';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { dmTeamService } from '@/services/dm-team.service';
import type { DmPage, DmShootingCategory, DmDesignCategory, DmAccessUser } from '@/services/dm-team.service';
import { usePermission } from '@/hooks/usePermission';
import { Tabs } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { usersService, type PickerUser } from '@/services/users.service';
import { DmUserMultiPicker } from '@/components/dm-team/DmUserMultiPicker';
import { Notice } from '@/components/ui/notice';

// ── Shared layout pieces ──────────────────────────────────────────────────────

const ITEM_GRID = 'grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4';
const DESIGN_GRID = 'grid items-start gap-3 md:grid-cols-2 xl:grid-cols-3';
const SECTION_GRID = 'grid items-start gap-4 lg:grid-cols-2';
const SKELETON_CARDS = 6;

/**
 * Loading / error / empty placeholder for a settings list. Renders nothing once
 * there is data, so callers place it right before their own grid.
 */
function ListState({
  isLoading, isError, onRetry, isEmpty, emptyText, grid = ITEM_GRID, skeletonHeight = 'h-16',
}: {
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  isEmpty: boolean;
  emptyText: string;
  grid?: string;
  skeletonHeight?: string;
}) {
  if (isLoading) {
    return (
      <div className={grid}>
        {[...Array(SKELETON_CARDS)].map((_, i) => (
          <div key={i} className={`${skeletonHeight} animate-pulse rounded-lg bg-muted/60`} />
        ))}
      </div>
    );
  }
  if (isError) {
    return (
      <Notice
        tone="danger"
        icon={AlertCircle}
        compact
        action={<Button size="sm" variant="outline" onClick={onRetry}>Retry</Button>}
      >
        Couldn’t load this list.
      </Notice>
    );
  }
  if (isEmpty) {
    return <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">{emptyText}</p>;
  }
  return null;
}

function TabHeader({ title, count, action }: { title: string; count?: number; action?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
        {count !== undefined && (
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium normal-case tracking-normal text-foreground">{count}</span>
        )}
      </h3>
      {action}
    </div>
  );
}

function initialsOf(first: string, last: string): string {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

// ── Access User Section (reusable for admin tabs) ─────────────────────────────

function AccessUserSection({
  title, description, type, color,
}: {
  title: string;
  description: string;
  type: DmAccessType;
  color: string;
}) {
  const qc = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { data: accessData, isLoading: accessLoading, isError: accessError, refetch: refetchAccess } = useQuery({
    queryKey: ['dm-team', 'access', type],
    queryFn: () => dmTeamService.listAccessUsers(type),
  });

  // /users/picker returns the whole active roster with departments and is open
  // to any signed-in user; list({ limit: 100 }) silently cut off past 100 staff.
  const { data: pickerUsers, isLoading: usersLoading } = useQuery({
    queryKey: ['dm-team', 'user-picker'],
    queryFn: () => usersService.picker(),
  });

  // The API adds one user per call, so a multi-pick fans out and reports once.
  const addMutation = useMutation({
    mutationFn: (ids: string[]) =>
      Promise.allSettled(ids.map((user_id) => dmTeamService.addAccessUser(type, { user_id } satisfies AddDmAccessUserInput))),
    onSuccess: (results) => {
      qc.invalidateQueries({ queryKey: ['dm-team', 'access', type] });
      setSelectedIds([]);
      const added = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - added;
      if (failed === 0) toast.success(added === 1 ? 'User added to access list' : `${added} users added to access list`);
      else if (added === 0) toast.error(failed === 1 ? 'Failed to add user' : `Failed to add ${failed} users`);
      else toast.warning(`${added} added, ${failed} failed`);
    },
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => dmTeamService.removeAccessUser(type, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dm-team', 'access', type] });
      toast.success('User removed');
    },
    onError: () => toast.error('Failed to remove user'),
  });

  const accessIds = new Set((accessData?.data ?? []).map((u: DmAccessUser) => u.user_id));
  const availableUsers = (pickerUsers ?? []).filter((u: PickerUser) => !accessIds.has(u.id));

  const members = accessData?.data ?? [];

  // No overflow-hidden on the card: the picker's panel is absolutely positioned
  // and would be clipped by it.
  return (
    <div className="rounded-lg border bg-card">
      <div className={`flex items-start justify-between gap-3 rounded-t-lg px-4 py-3 ${color}`}>
        <div className="min-w-0">
          <h4 className="font-semibold text-sm">{title}</h4>
          <p className="text-xs opacity-80 mt-0.5">{description}</p>
        </div>
        {!accessLoading && !accessError && (
          <span className="shrink-0 rounded-full bg-background/60 px-2 py-0.5 text-xs font-medium">
            {members.length} {members.length === 1 ? 'user' : 'users'}
          </span>
        )}
      </div>
      <div className="space-y-3 p-3">
      <div className="flex flex-col gap-2 sm:flex-row">
        <DmUserMultiPicker
          people={availableUsers}
          value={selectedIds}
          onChange={setSelectedIds}
          isLoading={usersLoading}
          disabled={addMutation.isPending}
        />
        <Button
          disabled={selectedIds.length === 0 || addMutation.isPending}
          onClick={() => addMutation.mutate(selectedIds)}
        >
          {selectedIds.length > 1 ? `Add (${selectedIds.length})` : 'Add'}
        </Button>
      </div>
      <ListState
        isLoading={accessLoading}
        isError={accessError}
        onRetry={() => refetchAccess()}
        isEmpty={members.length === 0}
        emptyText="No users in this list"
        grid="grid gap-2 sm:grid-cols-2"
        skeletonHeight="h-11"
      />
      {members.length > 0 && (
        <div className="thin-scrollbar grid max-h-72 gap-2 overflow-y-auto sm:grid-cols-2">
          {members.map((u: DmAccessUser) => (
            <div key={u.id} className="flex min-h-[44px] items-center gap-2.5 rounded-md border bg-background/40 px-2.5 py-1.5">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold">
                {initialsOf(u.first_name, u.last_name)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium" title={`${u.first_name} ${u.last_name}`}>{u.first_name} {u.last_name}</p>
                {u.employee_code && <p className="text-[11px] text-muted-foreground">{u.employee_code}</p>}
              </div>
              <Button
                variant="ghost" size="icon"
                className="h-8 w-8 sm:h-8 sm:w-8 shrink-0 text-muted-foreground hover:text-destructive"
                title="Remove"
                aria-label={`Remove ${u.first_name} ${u.last_name}`}
                disabled={removeMutation.isPending}
                onClick={() => removeMutation.mutate(u.user_id)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}

// ── Pages Tab ─────────────────────────────────────────────────────────────────

function PagesTab() {
  const qc = useQueryClient();
  const canManage = usePermission().has(PERMISSIONS.DM_TEAM_SETTINGS_MANAGE);
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState<DmPage | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['dm-team', 'pages'],
    queryFn: () => dmTeamService.listPages(),
  });

  const createForm = useForm<CreateDmPageInput>({ resolver: zodResolver(createDmPageSchema) });
  const editForm = useForm<UpdateDmPageInput>({ resolver: zodResolver(updateDmPageSchema) });

  const createMutation = useMutation({
    mutationFn: (body: CreateDmPageInput) => dmTeamService.createPage(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dm-team', 'pages'] }); setCreateOpen(false); createForm.reset(); toast.success('Page created'); },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Failed to create page'),
  });

  const editMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateDmPageInput }) => dmTeamService.updatePage(id, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dm-team', 'pages'] }); setEditItem(null); toast.success('Page updated'); },
    onError: () => toast.error('Failed to update page'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => dmTeamService.deletePage(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dm-team', 'pages'] }); setDeleteId(null); toast.success('Page deleted'); },
    onError: () => toast.error('Failed to delete page'),
  });

  const pages = data?.data ?? [];

  return (
    <div className="space-y-4">
      <TabHeader
        title="Page Sections"
        count={isLoading || isError ? undefined : pages.length}
        action={canManage && (
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-1" /> Add Page
          </Button>
        )}
      />
      <ListState isLoading={isLoading} isError={isError} onRetry={() => refetch()} isEmpty={pages.length === 0} emptyText="No pages yet" />
      <div className={ITEM_GRID}>
        {pages.map((page: DmPage) => (
          <div key={page.id} className="flex items-start justify-between gap-2 rounded-lg border bg-card p-3 transition-colors hover:border-primary/40">
            <div className="min-w-0">
              <p className="truncate font-medium text-sm" title={page.name}>{page.name}</p>
              {/* Most pages repeat their name as the description — skip the echo. */}
              {page.description && page.description.trim() !== page.name.trim() && (
                <p className="line-clamp-2 text-xs text-muted-foreground mt-0.5">{page.description}</p>
              )}
            </div>
            {canManage && (
              <div className="flex shrink-0 gap-0.5">
                <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-8 sm:w-8" onClick={() => { setEditItem(page); editForm.reset({ name: page.name, description: page.description ?? '' }); }}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-8 sm:w-8 text-destructive hover:text-destructive" onClick={() => setDeleteId(page.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Page</DialogTitle></DialogHeader>
          <form onSubmit={createForm.handleSubmit(d => createMutation.mutate(d))} className="space-y-4">
            <div><Label>Page Name *</Label><Input {...createForm.register('name')} placeholder="e.g. Rajarani Academy" className="mt-1" /></div>
            <div><Label>Description</Label><Textarea {...createForm.register('description')} className="mt-1 resize-none" rows={3} /></div>
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={createMutation.isPending}>Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editItem} onOpenChange={v => !v && setEditItem(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Page</DialogTitle></DialogHeader>
          <form onSubmit={editForm.handleSubmit(d => editItem && editMutation.mutate({ id: editItem.id, body: d }))} className="space-y-4">
            <div><Label>Page Name *</Label><Input {...editForm.register('name')} className="mt-1" /></div>
            <div><Label>Description</Label><Textarea {...editForm.register('description')} className="mt-1 resize-none" rows={3} /></div>
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setEditItem(null)}>Cancel</Button>
              <Button type="submit" disabled={editMutation.isPending}>Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={v => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Page?</AlertDialogTitle>
            <AlertDialogDescription>This will deactivate the page section.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteId && deleteMutation.mutate(deleteId)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Shooting Tab ──────────────────────────────────────────────────────────────

function ShootingTab() {
  const qc = useQueryClient();
  const canManage = usePermission().has(PERMISSIONS.DM_TEAM_SETTINGS_MANAGE);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['dm-team', 'shooting-categories'],
    queryFn: () => dmTeamService.listShootingCategories(false),
  });

  const form = useForm<CreateDmShootingCategoryInput>({ resolver: zodResolver(createDmShootingCategorySchema) });

  const createMutation = useMutation({
    mutationFn: (body: CreateDmShootingCategoryInput) => dmTeamService.createShootingCategory(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dm-team', 'shooting-categories'] }); setCreateOpen(false); form.reset(); toast.success('Category created'); },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Failed to create'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => dmTeamService.deleteShootingCategory(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dm-team', 'shooting-categories'] }); setDeleteId(null); toast.success('Category deleted'); },
    onError: () => toast.error('Failed to delete'),
  });

  const categories = data?.data ?? [];

  return (
    <div className="space-y-4">
      <TabHeader
        title="Shooting Categories"
        count={isLoading || isError ? undefined : categories.length}
        action={canManage && <Button size="sm" onClick={() => setCreateOpen(true)}><Plus className="w-4 h-4 mr-1" /> Add Category</Button>}
      />
      <ListState isLoading={isLoading} isError={isError} onRetry={() => refetch()} isEmpty={categories.length === 0} emptyText="No categories yet" />
      <div className={ITEM_GRID}>
        {categories.map((cat: DmShootingCategory) => (
          <div key={cat.id} className="flex items-start justify-between gap-2 rounded-lg border bg-card p-3 transition-colors hover:border-primary/40">
            <div className="min-w-0 space-y-1">
              <p className="truncate font-medium text-sm" title={cat.name}>{cat.name}</p>
              <Badge variant={cat.requires_location ? 'default' : 'secondary'} className="text-xs">
                {cat.requires_location ? 'Location Required' : 'Optional'}
              </Badge>
              {cat.description && <p className="line-clamp-2 text-xs text-muted-foreground">{cat.description}</p>}
            </div>
            {canManage && (
              <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-8 sm:w-8 shrink-0 text-destructive hover:text-destructive" onClick={() => setDeleteId(cat.id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Shooting Category</DialogTitle></DialogHeader>
          <form onSubmit={form.handleSubmit(d => createMutation.mutate(d))} className="space-y-4">
            <div><Label>Category Name *</Label><Input {...form.register('name')} className="mt-1" /></div>
            <div><Label>Description</Label><Textarea {...form.register('description')} className="mt-1 resize-none" rows={3} /></div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="req-loc" {...form.register('requires_location')} defaultChecked className="rounded" />
              <Label htmlFor="req-loc">Location is required</Label>
            </div>
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={createMutation.isPending}>Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={v => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Category?</AlertDialogTitle><AlertDialogDescription>This will deactivate the shooting category.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteId && deleteMutation.mutate(deleteId)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Design Tab ────────────────────────────────────────────────────────────────

function DesignTab() {
  const qc = useQueryClient();
  const canManage = usePermission().has(PERMISSIONS.DM_TEAM_SETTINGS_MANAGE);
  const [createOpen, setCreateOpen] = useState(false);
  const [sizeInput, setSizeInput] = useState('');
  const [pendingSizes, setPendingSizes] = useState<string[]>([]);
  const [addSizeCatId, setAddSizeCatId] = useState<string | null>(null);
  const [newSizeName, setNewSizeName] = useState('');
  const [editSize, setEditSize] = useState<{ catId: string; id: string; name: string } | null>(null);
  const [editSizeName, setEditSizeName] = useState('');
  const [pendingDeleteCat, setPendingDeleteCat] = useState<{ id: string; name: string } | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['dm-team', 'design-categories'],
    queryFn: () => dmTeamService.listDesignCategories(true),
  });

  const form = useForm<CreateDmDesignCategoryInput>({ resolver: zodResolver(createDmDesignCategorySchema) });

  const createMutation = useMutation({
    mutationFn: (body: CreateDmDesignCategoryInput) => dmTeamService.createDesignCategory(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dm-team', 'design-categories'] }); setCreateOpen(false); form.reset(); setPendingSizes([]); toast.success('Category created'); },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Failed to create'),
  });

  const addSizeMutation = useMutation({
    mutationFn: ({ categoryId, body }: { categoryId: string; body: AddDmDesignCategorySizeInput }) =>
      dmTeamService.addDesignCategorySize(categoryId, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dm-team', 'design-categories'] }); setAddSizeCatId(null); setNewSizeName(''); toast.success('Size added'); },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Failed to add size'),
  });

  const deleteSizeMutation = useMutation({
    mutationFn: ({ categoryId, sizeId }: { categoryId: string; sizeId: string }) =>
      dmTeamService.deleteDesignCategorySize(categoryId, sizeId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dm-team', 'design-categories'] }); toast.success('Size removed'); },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Failed to remove size'),
  });

  const editSizeMutation = useMutation({
    mutationFn: ({ catId, sizeId, name }: { catId: string; sizeId: string; name: string }) =>
      dmTeamService.updateDesignCategorySize(catId, sizeId, { name }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dm-team', 'design-categories'] }); setEditSize(null); toast.success('Size updated'); },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Failed to update size'),
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: (id: string) => dmTeamService.deleteDesignCategory(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dm-team', 'design-categories'] }); toast.success('Category deleted'); },
    onError: () => toast.error('Failed to delete category'),
  });

  const designCategories = data?.data ?? [];

  return (
    <div className="space-y-4">
      <TabHeader
        title="Design Categories"
        count={isLoading || isError ? undefined : designCategories.length}
        action={canManage && <Button size="sm" onClick={() => setCreateOpen(true)}><Plus className="w-4 h-4 mr-1" /> New Category</Button>}
      />
      <ListState isLoading={isLoading} isError={isError} onRetry={() => refetch()} isEmpty={designCategories.length === 0} emptyText="No categories yet" grid={DESIGN_GRID} skeletonHeight="h-24" />
      <div className={DESIGN_GRID}>
        {designCategories.map((cat: DmDesignCategory) => (
          <div key={cat.id} className="rounded-lg border bg-card p-3 space-y-2 transition-colors hover:border-primary/40">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate font-medium" title={cat.name}>{cat.name}</p>
                <p className="text-[11px] text-muted-foreground">{cat.sizes.length} {cat.sizes.length === 1 ? 'size' : 'sizes'}</p>
              </div>
              {canManage && (
                <div className="flex shrink-0 items-center gap-1">
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setAddSizeCatId(cat.id)}>
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add Size
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-8 sm:w-8 text-muted-foreground hover:text-destructive" title="Delete category" onClick={() => setPendingDeleteCat({ id: cat.id, name: cat.name })}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {cat.sizes.map(s => (
                <Badge key={s.id} variant="secondary" className="text-xs flex items-center gap-1">
                  {s.name}
                  {canManage && (
                    <>
                      <button onClick={() => { setEditSize({ catId: cat.id, id: s.id, name: s.name }); setEditSizeName(s.name); }} className="ml-0.5 hover:text-primary" title="Edit size"><Pencil className="h-2.5 w-2.5" /></button>
                      <button onClick={() => deleteSizeMutation.mutate({ categoryId: cat.id, sizeId: s.id })} className="hover:text-destructive" title="Remove size">×</button>
                    </>
                  )}
                </Badge>
              ))}
              {cat.sizes.length === 0 && <span className="text-xs text-muted-foreground">No sizes yet</span>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={createOpen} onOpenChange={v => { if (!v) { setPendingSizes([]); setSizeInput(''); } setCreateOpen(v); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Design Category</DialogTitle></DialogHeader>
          <form onSubmit={form.handleSubmit(d => createMutation.mutate({ ...d, sizes: pendingSizes.map((n, i) => ({ name: n, sort_order: i })) }))} className="space-y-4">
            <div><Label>Category Name *</Label><Input {...form.register('name')} className="mt-1" /></div>
            <div>
              <Label>Sizes</Label>
              <div className="flex gap-2 mt-1">
                <Input value={sizeInput} onChange={e => setSizeInput(e.target.value)} placeholder="e.g. Story-9:16" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (sizeInput.trim()) { setPendingSizes(p => [...p, sizeInput.trim()]); setSizeInput(''); } } }} />
                <Button type="button" variant="outline" onClick={() => { if (sizeInput.trim()) { setPendingSizes(p => [...p, sizeInput.trim()]); setSizeInput(''); } }}>+</Button>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {pendingSizes.map((s, i) => (
                  <Badge key={i} variant="secondary" className="text-xs">
                    {s} <button type="button" onClick={() => setPendingSizes(p => p.filter((_, j) => j !== i))} className="ml-1 hover:text-destructive">×</button>
                  </Badge>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={createMutation.isPending}>Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editSize} onOpenChange={v => !v && setEditSize(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Size</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Size Name *</Label><Input value={editSizeName} onChange={e => setEditSizeName(e.target.value)} className="mt-1" placeholder="e.g. Post-1080x1080px" /></div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditSize(null)}>Cancel</Button>
              <Button disabled={!editSizeName.trim() || editSizeMutation.isPending} onClick={() => editSize && editSizeMutation.mutate({ catId: editSize.catId, sizeId: editSize.id, name: editSizeName.trim() })}>Save</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!addSizeCatId} onOpenChange={v => !v && setAddSizeCatId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Size</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Size Name *</Label><Input value={newSizeName} onChange={e => setNewSizeName(e.target.value)} className="mt-1" placeholder="e.g. Post-1080x1080px" /></div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddSizeCatId(null)}>Cancel</Button>
              <Button disabled={!newSizeName.trim() || addSizeMutation.isPending} onClick={() => addSizeCatId && addSizeMutation.mutate({ categoryId: addSizeCatId, body: { name: newSizeName.trim(), sort_order: 0 } })}>Add</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!pendingDeleteCat}
        onOpenChange={(o) => { if (!o) setPendingDeleteCat(null); }}
        title={`Delete category "${pendingDeleteCat?.name}"?`}
        description="This will permanently delete the category and all its sizes."
        confirmLabel="Delete"
        onConfirm={() => { if (pendingDeleteCat) deleteCategoryMutation.mutate(pendingDeleteCat.id); }}
      />
    </div>
  );
}

// ── Admin access tab (reused pattern) ─────────────────────────────────────────

function DesignUsersTab() {
  return (
    <div className={SECTION_GRID}>
      <AccessUserSection type="design_category_access" title="Design Categories Access" description="These users can manage design categories in Settings" color="bg-purple-50 text-purple-900 dark:bg-purple-950 dark:text-purple-100" />
      <AccessUserSection type="designer_status_access" title="Status Update & Assigned By Access" description="These users can change the status of design projects, pick Assigned By from the dropdown, and see all completed projects" color="bg-orange-50 text-orange-900 dark:bg-orange-950 dark:text-orange-100" />
      <AccessUserSection type="designer_completed_access" title="View All Active & Cancelled Projects" description="These users can see active and cancelled projects of other designers" color="bg-green-50 text-green-900 dark:bg-green-950 dark:text-green-100" />
      <AccessUserSection type="designer_reorder_access" title="Drag to Reorder Access" description="These users can drag and reorder design projects on the designer board" color="bg-blue-50 text-blue-900 dark:bg-blue-950 dark:text-blue-100" />
      <AccessUserSection type="designer_delete_access" title="Delete Project Access" description="These users can delete design projects from the designer board" color="bg-red-50 text-red-900 dark:bg-red-950 dark:text-red-100" />
    </div>
  );
}

function ScheduleTab() {
  return (
    <div className={SECTION_GRID}>
      <AccessUserSection type="schedule_delete_permission" title="Schedule Calendar Delete Permission" description="Only users listed here can delete activities from the Schedule Calendar" color="bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-100" />
    </div>
  );
}

function DmActivitiesTab() {
  return (
    <div className={SECTION_GRID}>
      <AccessUserSection type="activities_delete_permission" title="Activities Delete Permission" description="Only users listed here can delete activities from the DM Activities (Flow) view" color="bg-red-50 text-red-900 dark:bg-red-950 dark:text-red-100" />
      <AccessUserSection type="complete_list_delete_permission" title="Complete List Delete Permission" description="Only users listed here can delete activities from the Complete list view" color="bg-red-50 text-red-900 dark:bg-red-950 dark:text-red-100" />
    </div>
  );
}

function ShootTeamTab() {
  return (
    <div className={SECTION_GRID}>
      <AccessUserSection type="shoot_team_members" title="Shoot Team (Who Shot)" description="These users appear in the 'Who Shot' dropdown in shoot activities" color="bg-teal-50 text-teal-900 dark:bg-teal-950 dark:text-teal-100" />
      <AccessUserSection type="assign_list_members" title="Assign List" description="These users appear in the 'Assigned' dropdown in shoot activities" color="bg-teal-50 text-teal-900 dark:bg-teal-950 dark:text-teal-100" />
    </div>
  );
}

// ── Main Settings Page ────────────────────────────────────────────────────────

const CONTENT_MAP: Record<string, React.ReactNode> = {
  pages: <PagesTab />,
  shooting: <ShootingTab />,
  design: <DesignTab />,
  'design-users': <DesignUsersTab />,
  schedule: <ScheduleTab />,
  'dm-activities': <DmActivitiesTab />,
  'shoot-team': <ShootTeamTab />,
};

export function DmSettingsPage() {
  const canManage = usePermission().has(PERMISSIONS.DM_TEAM_SETTINGS_MANAGE);
  const [activeTab, setActiveTab] = useState('pages');

  const baseTabs = [
    { key: 'pages', label: 'Pages' },
    { key: 'shooting', label: 'Shooting' },
    { key: 'design', label: 'Design' },
  ];

  const adminTabDefs = [
    { key: 'design-users', label: canManage ? 'Design Users' : '🔒 Design Users' },
    { key: 'schedule', label: canManage ? 'Schedule' : '🔒 Schedule' },
    { key: 'dm-activities', label: canManage ? 'DM Activities' : '🔒 DM Activities' },
    { key: 'shoot-team', label: canManage ? 'Shoot Team' : '🔒 Shoot Team' },
  ];

  const allTabs = [...baseTabs, ...adminTabDefs];

  const handleTabChange = (key: string) => {
    const isAdmin = ['design-users', 'schedule', 'dm-activities', 'shoot-team'].includes(key);
    if (isAdmin && !canManage) return;
    setActiveTab(key);
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">DM Team Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Configure pages, categories, and access control for the DM Team module</p>
      </div>

      <Tabs tabs={allTabs} active={activeTab} onChange={handleTabChange} />

      <div className="mt-2">
        {CONTENT_MAP[activeTab]}
      </div>
    </div>
  );
}
